import { GLOBAL_ASSETS_SENTINEL_OWNER_ID } from '@cadstrom/cadstrom-shared';
import SyncIcon from '@mui/icons-material/Sync';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useMutationState, useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatchRoute } from '@tanstack/react-router';
import clsx from 'clsx';
import { Suspense, useMemo, useState } from 'react';

import { CommandBarPriority } from '#components/command-bar/command-bar-types.js';
import { CommandBarItem } from '#components/command-bar/components/command-bar-item.js';
import {
	PAGE_NAV_LABEL_PREFIX,
	RegisterPageNavigationProvider,
} from '#components/command-bar/providers/page-navigation-provider.js';
import { InlineSkeleton } from '#components/inline-skeleton/inline-skeleton.js';
import { Loading } from '#components/loading.js';
import { SignalReferenceContextProvider } from '#components/markdown/plugins/signal-reference-context-provider.js';
import { ResizableLayout } from '#components/resizable-layout/resizable-layout.js';
import { SidePanelTab } from '#components/side-panel-tabs/side-panel-tab.js';
import { SidePanelTabs } from '#components/side-panel-tabs/side-panel-tabs.js';
import { Tabs } from '#components/tabs/tabs.js';
import { PackageEvidencePanel } from '#package-store/package-family-editor/package-evidence-panel/package-evidence-panel.js';
import { usePackageSidePanelStore } from '#package-store/package-family-editor/side-panel/package-side-panel-store.js';
import {
	AddAgentWorkbenchPageContext,
	AgentWorkbenchPageContextProvider,
} from '#screens/agent-workbench/agent-workbench-page-context.js';
import {
	AddAiExtractionContextPkgFamily,
	AddAiExtractionContextPkgs,
	AddAiExtractionContextTemplates,
	AiExtractionContext,
	useAiExtractionContext,
} from '#screens/ai-workbench/ai-extraction-context.js';
import { AiWorkbench } from '#screens/ai-workbench/ai-workbench.js';
import { useTRPC } from '#src/trpc.js';
import { useSidebarIsFloating } from '#state/atoms/sidebar-layout.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { useOrganizationById } from '#utils/use-organization-by-id.js';
import { PackageFamilyAgentWorkbench } from './package-family-agent-workbench.js';

import type { TabDef } from '#components/tabs/tabs.js';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'packageFamily'),
	},
	beforeLoad: ({ context: { trpc }, params: { packageFamilyId } }) => {
		return {
			getPackageFamilyOptions: trpc.packageFamily.get.queryOptions({ id: packageFamilyId }, { refetchOnMount: false }),
			getFamilyPackagesOptions: trpc.package.getMany.queryOptions(
				{ pkgFamilyId: packageFamilyId },
				{ refetchOnMount: false },
			),
		};
	},
	loader: ({ context: { queryClient, getPackageFamilyOptions } }) => {
		void queryClient.ensureQueryData(getPackageFamilyOptions);
	},
	head: () => ({ meta: [{ title: 'Package Editor • Cadstrom' }] }),
	component: PackageFamilyEditorLayout,
});

function PackageFamilyEditorLayout() {
	const trpc = useTRPC();
	const sidebarIsFloating = useSidebarIsFloating();

	const { getPackageFamilyOptions, getFamilyPackagesOptions } = Route.useRouteContext();

	const { data: pkgFamilyData } = useQuery(getPackageFamilyOptions);
	const { data: pkgsData } = useQuery(getFamilyPackagesOptions);
	const { data: manufacturerData } = useQuery(
		trpc.manufacturer.get.queryOptions(
			{ id: pkgFamilyData?.pkgFamily.manufacturerId ?? '' },
			{ staleTime: Infinity, enabled: pkgFamilyData !== undefined },
		),
	);

	const tabDefs: TabDef[] = [
		{ id: 'general', label: 'General', linkProps: { to: '/package-store/$packageFamilyId/general' } },
		{ id: 'packages', label: 'Packages', linkProps: { to: '/package-store/$packageFamilyId/packages' } },
		{ id: 'raw', label: 'Raw', linkProps: { to: '/package-store/$packageFamilyId/raw' } },
	];

	const pkgFamily = pkgFamilyData?.pkgFamily;
	const pkgs = pkgsData?.pkgs;
	const manufacturer = manufacturerData?.manufacturer;

	const { displayName: ownerDisplayName } = useOrganizationById(pkgFamily?.ownerId);
	const isConfidential = pkgFamily !== undefined && pkgFamily.ownerId !== GLOBAL_ASSETS_SENTINEL_OWNER_ID;

	const matchRoute = useMatchRoute();
	const currentTab = tabDefs.find(({ linkProps }) => matchRoute({ ...linkProps, fuzzy: true }) !== false);
	const aiExtractionContext = useAiExtractionContext();

	const templates = useMemo(
		() =>
			pkgFamily ?
				[{ name: 'pkgFamily', value: () => pkgFamily.name, description: 'The name of the package family' }]
			:	[],
		[pkgFamily],
	);

	// First-wins across the family's packages (stable query order), so a name shared by
	// several packages resolves consistently in workbench signal references.
	const workbenchSignals = useMemo(() => pkgs?.flatMap((pkg) => pkg.signals) ?? [], [pkgs]);

	const pendingPackageFamilyMutations = useMutationState({
		filters: { mutationKey: trpc.packageFamily.pathKey(), status: 'pending' },
	});
	const pendingPackageMutations = useMutationState({
		filters: { mutationKey: trpc.package.pathKey(), status: 'pending' },
	});
	const isMutating = pendingPackageFamilyMutations.length > 0 || pendingPackageMutations.length > 0;
	const [useLegacyWorkbench, setUseLegacyWorkbench] = useState(false);
	const sidePanelTab = usePackageSidePanelStore((state) => state.selectedTab);
	const setSidePanelTab = usePackageSidePanelStore((state) => state.selectTab);

	const el = useDataElTagGenerator(PackageFamilyEditorLayout);
	return (
		<AiExtractionContext.Provider value={aiExtractionContext}>
			<AgentWorkbenchPageContextProvider>
				<AddAgentWorkbenchPageContext
					label='Package Store'
					context={{
						'Package family': pkgFamily?.name,
						Manufacturer: manufacturer?.name,
						// Who owns the family, so the agent can say whose part it is. Whether that
						// ownership makes it confidential is the backend's call, not this value's.
						Owner: isConfidential ? (ownerDisplayName ?? 'A private organization') : 'Cadstrom global assets',
					}}
					analytics={{ package_family: pkgFamily?.name, manufacturer: manufacturer?.name }}
				/>
				<ResizableLayout {...el()} split='vertical' initialProportions={['3fr', '1fr']}>
					<div {...el('editor')} className='flex grow flex-col overflow-hidden'>
						<div {...el('header')} className='flex flex-col gap-3 border-b border-gray-a4 p-4 pb-0'>
							<div className={clsx('flex items-start justify-between gap-4 self-stretch', sidebarIsFloating && 'pl-6')}>
								<div className='flex flex-col gap-2'>
									<InlineSkeleton
										data={[pkgFamily]}
										slotProps={{ skeleton: { width: 280, height: '1lh', className: 'text-xl' } }}
										render={(pkgFamily) => (
											<div className='flex items-center gap-3'>
												<h2 className='text-xl font-semibold'>Package Family {pkgFamily.name}</h2>
												{isConfidential && (
													<Tooltip
														title={
															ownerDisplayName !== undefined ?
																`Private package owned by ${ownerDisplayName}`
															:	'Private package owned by a private organization'
														}
													>
														<Chip
															size='small'
															variant='outlined'
															color='primary'
															label='Confidential'
															aria-label='Package family is confidential'
														/>
													</Tooltip>
												)}
											</div>
										)}
									/>
									<InlineSkeleton
										data={[manufacturer]}
										slotProps={{ skeleton: { width: 120, height: '1lh', className: 'text-sm' } }}
										render={(manufacturer) => (
											<p className='text-sm text-gray-a8'>
												{manufacturer.name}
												{isConfidential && <> — {ownerDisplayName ?? 'Private organization'}</>}
											</p>
										)}
									/>
								</div>

								{isMutating && (
									<div className='flex animate-pulse items-center gap-1 [animation-duration:5s]'>
										<SyncIcon className='animate-spin text-orange-9 [animation-direction:reverse] [animation-duration:3s]' />
										<p className='font-medium text-orange-a11'>Saving...</p>
									</div>
								)}
							</div>

							<Tabs tabs={tabDefs} selectedTab={currentTab?.id ?? null} />
						</div>

						<div {...el('outlet')} className='flex grow basis-0 flex-col overflow-auto'>
							<Suspense fallback={<Loading />}>
								<Outlet key={pkgFamily?.id} />
							</Suspense>
						</div>
					</div>
					<SignalReferenceContextProvider signals={workbenchSignals}>
						<div
							{...el('aiWorkbench')}
							className='m-2 ml-0 flex min-h-0 min-w-0 grow basis-0 flex-col rounded-lg border border-gray-a3 shadow-xs-blurlg'
						>
							<SidePanelTabs
								selectedTab={sidePanelTab}
								onSelectedTabChange={setSidePanelTab}
								workbenchTabAdornment={
									<ButtonBase
										className='text-gray-a9 hover:text-gray-a11'
										onClick={() => setUseLegacyWorkbench((prev) => !prev)}
									>
										{useLegacyWorkbench ? 'Switch to agent' : 'Classic AI'}
									</ButtonBase>
								}
							/>
							{/* Keyed by family, not by the toggle: the runtime holds the active conversation, so it
							    must outlive a switch to the classic workbench but must not follow the operator to a
							    different family, whose page context the agent would then answer under. */}
							<PackageFamilyAgentWorkbench
								key={pkgFamily?.id}
								isHidden={useLegacyWorkbench || sidePanelTab !== SidePanelTab.Workbench}
							/>
							{sidePanelTab === SidePanelTab.Workbench && useLegacyWorkbench && <AiWorkbench disableDownload />}
							{sidePanelTab === SidePanelTab.Evidence && <PackageEvidencePanel datasheetWindowEvidence />}
						</div>
					</SignalReferenceContextProvider>
				</ResizableLayout>

				<AddAiExtractionContextTemplates value={templates} />
				{pkgFamily && <AddAiExtractionContextPkgFamily value={pkgFamily} />}
				{pkgs && <AddAiExtractionContextPkgs value={pkgs} />}
				{pkgFamily &&
					pkgs?.map((pkg) => (
						<RegisterPageNavigationProvider
							key={pkg.id}
							route={{
								label: (
									<CommandBarItem name={PAGE_NAV_LABEL_PREFIX}>
										<span className='command-bar-item-instance'> {pkg.manufacturerPartNumber}</span>
									</CommandBarItem>
								),
								to: '/package-store/$packageFamilyId/packages/$packageId',
								params: { packageFamilyId: pkgFamily.id, packageId: pkg.id },
							}}
							defaultRelevance={CommandBarPriority.High}
						/>
					))}
			</AgentWorkbenchPageContextProvider>
		</AiExtractionContext.Provider>
	);
}
